import {ReactIsland} from "@/shared/reactIsland";
import type {PasswordStrengthValidation} from "@/features/auth/passwordStrengthValidation";
import {UserSettingsApp, type UserSettingsAppProps} from "./settings/userSettingsApp";
import {getAppearancePreference} from "@/shared/appearance";

export interface ExternalAuthConnection {
	connectedAs: string | null;
	/** Pre-formatted with the client's date format and time zone. */
	connectedAt: string;
}

export interface CsrfConfig {
	parameterName: string;
	token: string;
}

/** Boot config passed from the JSP via the {@code jsConstants} view model. */
export interface UserSettingsConfig {
	clientIdentifier: string;
	accountOwner: string;
	showChangePasswordButton: boolean;
	newsletter: boolean;
	runningTimerWarningAfterHour: number | null;
	workingTimeRequestStatusEmail: boolean;
	/** Feature check plus client flag — unlike {@code globals.workingTimeRequestsEnabled}. */
	workingTimeRequestsEnabled: boolean;
	googleLoginAvailable: boolean;
	microsoftLoginAvailable: boolean;
	externalAuthOnly: boolean;
	externalAuthConnections: Partial<Record<"google" | "azure", ExternalAuthConnection>>;
	showCreateTerminalConfigPanel: boolean;
	/** Google Workspace integration home, or null when the integration is not enabled for the client. */
	googleSyncUrl: string | null;
	/** Microsoft workspace integration home ("Outlook"), or null when the integration is not enabled for the client. */
	microsoftSyncUrl: string | null;
	calendarSubscriptionToken: string | null;
	connectedMobileDevices: number;
	timrBaseUrl: string;
	absenceCalendarSubscriptionToken: string | null;
	showTeammemberAbsenceCalendar: boolean;
	showAllAbsenceCalendar: boolean;
	passwordStrengthValidation?: PasswordStrengthValidation;
	/* set by the JSP itself, not part of the jsConstants view model */
	csrf?: CsrfConfig;
}

const MOUNT_ID = "userSettingsContent";

export class UserSettingsPage {
	private readonly island: ReactIsland<UserSettingsAppProps>;

	constructor(config: UserSettingsConfig) {
		const container = document.getElementById(MOUNT_ID);
		if (!container) {
			throw new Error(`React mount target #${MOUNT_ID} was not found`);
		}
		const appearancePreference = document.documentElement.classList.contains("dark-mode-available")
			? getAppearancePreference() : null;
		this.island = new ReactIsland<UserSettingsAppProps>(UserSettingsApp, {config, appearancePreference}, container);
	}

	public destroy() {
		this.island.destroy();
	}
}
